/**
 * @fileoverview
 * Coordinates inactivity detection, cross-tab sync, warning countdown, and session extension.
 * Auth mutations go through AuthService (centralized).
 */

import { Injectable, inject, signal, computed, NgZone, OnDestroy } from '@angular/core';
import { AuthService } from './auth.service';
import { STORAGE_KEYS } from '../../constants/storage-keys.const';
import { StorageUtil } from '../../utils/storage.utils';
import { environment } from '../../../../environments/environment';

const LOG = '[InactivitySession]';
const SYNC_CHANNEL = 'ews_admin_session_sync';

type SyncMessage =
  | { type: 'user-activity'; at: number }
  | { type: 'warning-dismissed' }
  | { type: 'stay-logged-in-ok'; at: number };

@Injectable({
  providedIn: 'root',
})
export class InactivitySessionManagerService implements OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly zone = inject(NgZone);

  private readonly inactivityMs = environment.sessionInactivityMs ?? 2 * 60 * 1000;
  private readonly warningResponseMs =
    environment.sessionWarningResponseMs ?? 60 * 1000;
  private readonly activityThrottleMs =
    environment.sessionActivityThrottleMs ?? 2000;

  private monitoring = false;
  private lastLocalActivityMs = 0;
  private lastThrottleWriteMs = 0;
  private isRefreshing = false;
  /** Do not reset in {@link stop} — only in {@link start} and after terminal logout. */
  private autoLogoutTriggered = false;

  private pollHandle: ReturnType<typeof setInterval> | null = null;
  private countdownTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly activityListeners: Array<{
    type: string;
    fn: EventListener;
    opts?: AddEventListenerOptions;
  }> = [];

  private readonly bc: BroadcastChannel | null =
    typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(SYNC_CHANNEL);

  private readonly remainingSecondsSignal = signal<number | null>(null);
  private readonly warningActiveSignal = signal(false);

  readonly remainingSeconds = this.remainingSecondsSignal.asReadonly();
  readonly showWarning = computed(() => this.warningActiveSignal());

  ngOnDestroy(): void {
    this.teardownListeners();
    this.clearPoll();
    this.clearCountdown();
    this.bc?.close();
  }

  start(): void {
    this.stopInternal(false);
    this.monitoring = true;
    this.autoLogoutTriggered = false;
    this.lastLocalActivityMs = Date.now();
    this.persistActivityThrottled(this.lastLocalActivityMs, true);
    this.clearWarningStorage();
    this.warningActiveSignal.set(false);
    this.remainingSecondsSignal.set(null);

    console.info(`${LOG} inactivity monitoring started`, {
      inactivityMs: this.inactivityMs,
      warningMs: this.warningResponseMs,
    });

    this.attachListeners();
    this.startPoll();
    this.bc?.addEventListener('message', this.onBroadcastMessage);

    this.zone.runOutsideAngular(() => {
      globalThis.addEventListener('storage', this.onStorageEvent);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    });
  }

  /**
   * Stops listeners and timers. Resets {@link autoLogoutTriggered} so a later login can arm again.
   */
  stop(): void {
    this.stopInternal(true);
  }

  private stopInternal(clearAutoLogoutFlag: boolean): void {
    this.monitoring = false;
    this.isRefreshing = false;
    if (clearAutoLogoutFlag) {
      this.autoLogoutTriggered = false;
    }
    this.teardownListeners();
    this.clearPoll();
    this.clearCountdown();
    this.remainingSecondsSignal.set(null);
    this.warningActiveSignal.set(false);
    this.bc?.removeEventListener('message', this.onBroadcastMessage);
    globalThis.removeEventListener('storage', this.onStorageEvent);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  recordUserActivity(): void {
    this.pulseIdleActivity();
  }

  recordHttpActivity(): void {
    this.pulseIdleActivity();
  }

  private pulseIdleActivity(): void {
    if (!this.monitoring || this.warningActiveSignal()) {
      return;
    }
    this.applyActivity(Date.now(), 'local');
  }

  stayLoggedIn(): void {
    if (!this.monitoring || this.isRefreshing) return;

    console.info(`${LOG} Stay Logged In — calling refresh token`);
    this.isRefreshing = true;
    this.clearWarningUiState();

    this.authService.refreshToken().subscribe({
      next: () => {
        this.isRefreshing = false;
        const at = Date.now();
        this.applyActivity(at, 'local', true);
        this.broadcast({ type: 'stay-logged-in-ok', at });
        this.start();
      },
      error: () => {
        console.warn(`${LOG} refresh failed after Stay Logged In — session cleared by AuthService`);
        this.isRefreshing = false;
      },
    });
  }

  logOut(): void {
    console.info(`${LOG} manual logout from session modal`);
    this.clearWarningStorage();
    this.broadcast({ type: 'warning-dismissed' });
    this.stopInternal(true);
    this.authService.logout();
  }

  private readonly onBroadcastMessage = (ev: MessageEvent<SyncMessage>): void => {
    const data = ev.data;
    if (!data || typeof data !== 'object') return;
    if (data.type === 'user-activity') {
      if (!this.warningActiveSignal()) {
        this.applyActivity(data.at, 'remote', false);
      }
    } else if (data.type === 'warning-dismissed' || data.type === 'stay-logged-in-ok') {
      this.zone.run(() => {
        this.clearCountdown();
        this.warningActiveSignal.set(false);
        this.remainingSecondsSignal.set(null);
        this.clearWarningStorage();
        if (data.type === 'stay-logged-in-ok') {
          this.lastLocalActivityMs = data.at;
        }
      });
    }
  };

  private readonly onStorageEvent = (e: StorageEvent): void => {
    if (e.key === STORAGE_KEYS.LAST_USER_ACTIVITY_MS && e.newValue) {
      if (this.warningActiveSignal()) {
        return;
      }
      let t: number;
      try {
        t = Number(JSON.parse(e.newValue));
      } catch {
        t = Number(e.newValue);
      }
      if (!Number.isFinite(t)) return;
      this.applyActivity(t, 'remote', false);
    }
    if (e.key === STORAGE_KEYS.SESSION_WARNING_DEADLINE_MS) {
      if (e.newValue === null) {
        this.zone.run(() => {
          this.clearCountdown();
          this.warningActiveSignal.set(false);
          this.remainingSecondsSignal.set(null);
        });
      } else {
        this.zone.run(() => this.evaluateIdleState());
      }
    }
  };

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.zone.run(() => this.evaluateIdleState());
    }
  };

  private applyActivity(at: number, source: 'local' | 'remote', forceWrite = false): void {
    if (source === 'local') {
      this.lastLocalActivityMs = at;
    }

    if (source === 'local') {
      const wrote = this.persistActivityThrottled(at, forceWrite);
      if (wrote) {
        this.broadcast({ type: 'user-activity', at });
      }
    }

    this.zone.run(() => this.evaluateIdleState());
  }

  private effectiveLastActivityMs(): number {
    const fromDisk = StorageUtil.getLocal<string>(STORAGE_KEYS.LAST_USER_ACTIVITY_MS);
    const parsed = fromDisk ? Number(fromDisk) : 0;
    return Math.max(this.lastLocalActivityMs, Number.isFinite(parsed) ? parsed : 0);
  }

  private persistActivityThrottled(at: number, force: boolean): boolean {
    const now = Date.now();
    if (!force && now - this.lastThrottleWriteMs < this.activityThrottleMs) {
      return false;
    }
    this.lastThrottleWriteMs = now;
    StorageUtil.setLocal(STORAGE_KEYS.LAST_USER_ACTIVITY_MS, String(at));
    return true;
  }

  private broadcast(msg: SyncMessage): void {
    try {
      this.bc?.postMessage(msg);
    } catch {
      /* ignore */
    }
  }

  private attachListeners(): void {
    const handler = (): void => {
      this.recordUserActivity();
    };

    const opts: AddEventListenerOptions = { passive: true, capture: true };
    const types = [
      'mousemove',
      'mousedown',
      'mouseup',
      'click',
      'keydown',
      'keyup',
      'scroll',
      'wheel',
      'touchstart',
      'pointerdown',
    ] as const;

    this.zone.runOutsideAngular(() => {
      for (const type of types) {
        globalThis.addEventListener(type, handler, opts);
        this.activityListeners.push({ type, fn: handler, opts });
      }
    });
  }

  private teardownListeners(): void {
    for (const { type, fn, opts } of this.activityListeners) {
      globalThis.removeEventListener(type, fn, opts);
    }
    this.activityListeners.length = 0;
  }

  private startPoll(): void {
    this.clearPoll();
    this.pollHandle = setInterval(() => {
      this.zone.run(() => this.evaluateIdleState());
    }, 1000);
  }

  private clearPoll(): void {
    if (this.pollHandle !== null) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  }

  private evaluateIdleState(): void {
    if (!this.monitoring || !this.authService.isAuthenticated() || this.isRefreshing) return;

    if (this.warningActiveSignal()) {
      this.syncWarningCountdownFromStorage();
      return;
    }

    const idleFor = Date.now() - this.effectiveLastActivityMs();
    const rawDeadline = StorageUtil.getLocal<string>(STORAGE_KEYS.SESSION_WARNING_DEADLINE_MS);
    const existingDeadline = rawDeadline ? Number(rawDeadline) : Number.NaN;

    if (this.tryHandleExpiredDeadline(existingDeadline)) return;
    if (this.tryJoinActiveWarningCountdown(existingDeadline)) return;
    if (idleFor >= this.inactivityMs) {
      this.openWarning();
      return;
    }
    this.clearOrphanWarningUi(existingDeadline);
  }

  private syncWarningCountdownFromStorage(): void {
    const rawDeadline = StorageUtil.getLocal<string>(STORAGE_KEYS.SESSION_WARNING_DEADLINE_MS);
    const existingDeadline = rawDeadline ? Number(rawDeadline) : Number.NaN;

    if (!Number.isFinite(existingDeadline)) {
      this.clearCountdown();
      this.warningActiveSignal.set(false);
      this.remainingSecondsSignal.set(null);
      return;
    }

    if (existingDeadline <= Date.now()) {
      this.triggerAutoLogoutOnce();
      return;
    }

    if (!this.countdownTimeoutId) {
      this.startCountdownFromDeadline(existingDeadline);
    }
  }

  private tryHandleExpiredDeadline(existingDeadline: number): boolean {
    if (!Number.isFinite(existingDeadline) || existingDeadline > Date.now()) {
      return false;
    }
    if (this.warningActiveSignal()) {
      this.triggerAutoLogoutOnce();
    } else {
      this.clearWarningStorage();
    }
    return true;
  }

  private tryJoinActiveWarningCountdown(existingDeadline: number): boolean {
    if (!Number.isFinite(existingDeadline) || existingDeadline <= Date.now()) {
      return false;
    }
    if (!this.warningActiveSignal()) {
      this.warningActiveSignal.set(true);
    }
    if (!this.countdownTimeoutId) {
      this.startCountdownFromDeadline(existingDeadline);
    }
    return true;
  }

  private clearOrphanWarningUi(existingDeadline: number): void {
    if (this.warningActiveSignal() && !Number.isFinite(existingDeadline)) {
      this.clearCountdown();
      this.warningActiveSignal.set(false);
      this.remainingSecondsSignal.set(null);
    }
  }

  private openWarning(): void {
    if (this.warningActiveSignal()) {
      const raw = StorageUtil.getLocal<string>(STORAGE_KEYS.SESSION_WARNING_DEADLINE_MS);
      const d = raw ? Number(raw) : Number.NaN;
      if (Number.isFinite(d) && d > Date.now() && !this.countdownTimeoutId) {
        this.startCountdownFromDeadline(d);
      }
      return;
    }

    const raw = StorageUtil.getLocal<string>(STORAGE_KEYS.SESSION_WARNING_DEADLINE_MS);
    let deadline: number;
    const parsed = raw ? Number(raw) : Number.NaN;
    if (Number.isFinite(parsed) && parsed > Date.now()) {
      deadline = parsed;
    } else {
      deadline = Date.now() + this.warningResponseMs;
      StorageUtil.setLocal(STORAGE_KEYS.SESSION_WARNING_DEADLINE_MS, String(deadline));
    }

    console.info(`${LOG} session timeout popup opened`, { deadline });
    this.warningActiveSignal.set(true);
    this.startCountdownFromDeadline(deadline);
  }

  private startCountdownFromDeadline(deadline: number): void {
    this.clearCountdown();
    console.info(`${LOG} warning countdown timer started`, {
      deadline,
      seconds: Math.ceil((deadline - Date.now()) / 1000),
    });

    const tick = (): void => {
      this.countdownTimeoutId = null;
      const remainingMs = deadline - Date.now();
      const sec = Math.max(0, Math.ceil(remainingMs / 1000));
      this.remainingSecondsSignal.set(sec);

      if (remainingMs <= 0) {
        this.triggerAutoLogoutOnce();
        return;
      }

      this.countdownTimeoutId = setTimeout(() => {
        this.zone.run(tick);
      }, 1000);
    };

    this.zone.run(tick);
  }

  private clearCountdown(): void {
    if (this.countdownTimeoutId !== null) {
      clearTimeout(this.countdownTimeoutId);
      this.countdownTimeoutId = null;
    }
  }

  private triggerAutoLogoutOnce(): void {
    if (this.autoLogoutTriggered) return;
    this.autoLogoutTriggered = true;
    console.warn(`${LOG} auto logout triggered (warning expired)`);
    this.clearWarningStorage();
    // Do not reset autoLogoutTriggered here — keep true until a full `stop()` after auth clears.
    this.stopInternal(false);
    this.zone.run(() => {
      this.authService.logout();
    });
  }

  private clearWarningUiState(): void {
    this.clearCountdown();
    this.clearWarningStorage();
    this.warningActiveSignal.set(false);
    this.remainingSecondsSignal.set(null);
  }

  private clearWarningStorage(): void {
    StorageUtil.removeLocal(STORAGE_KEYS.SESSION_WARNING_DEADLINE_MS);
  }
}
